import { useState, useEffect } from "react";
import { t } from "@/lib/i18n";
import { Buildings, EnvelopeSimple, Brain, ClockCounterClockwise, CaretRight } from "@phosphor-icons/react";
import dayjs from "dayjs";
import { PageShell } from "@/components/PageShell";
import { MailboxSwitcher, useMailboxApi } from "@/components/MailboxSwitcher";
import api from "@/lib/api";

export default function ContactsPage() {
  const { mailbox, setMailbox, mbApi } = useMailboxApi();
  const [contacts, setContacts] = useState([]);
  const [detail, setDetail] = useState(null);

  useEffect(() => { setDetail(null); mbApi.get("/contacts").then((r) => setContacts(r.data)); }, [mbApi]);

  const openDetail = async (c) => {
    const { data } = await mbApi.get(`/contacts/${c.id}`);
    setDetail(data);
  };

  return (
    <PageShell title={t("con.title", "Address Book")} subtitle={t("con.subtitle", "Every person's history and memory the agent uses to personalize replies.")} testId="contacts-page">
      <MailboxSwitcher mailbox={mailbox} onChange={setMailbox} />
      <div className="grid grid-cols-5 gap-6">
        <div className="col-span-2 space-y-1.5">
          {contacts.map((c) => (
            <button key={c.id} data-testid={`contact-row-${c.id}`} onClick={() => openDetail(c)}
              className={`w-full text-left flex items-center gap-3 border rounded-md px-3 py-2.5 transition-colors ${detail?.contact?.id === c.id ? "border-blue-300 bg-blue-50" : "border-gray-200 hover:bg-gray-50"}`}>
              <div className="w-9 h-9 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-bold shrink-0">
                {c.name[0]}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-gray-900 truncate flex items-center gap-1">
                  {c.name}
                  {c.is_vip && <span data-testid={`vip-badge-${c.id}`} title={t("con.vip_title", "VIP contact")} className="text-amber-500 text-xs">★</span>}
                </div>
                <div className="text-xs text-gray-500 truncate">{c.organization}</div>
              </div>
              <CaretRight size={13} className="text-gray-300" />
            </button>
          ))}
          {contacts.length === 0 && <p className="text-sm text-gray-400">{t("con.none", "No contacts yet.")}</p>}
        </div>

        <div className="col-span-3">
          {!detail ? (
            <div className="border border-dashed border-gray-300 rounded-md p-10 text-center text-sm text-gray-400">{t("con.select", "Select a contact to see their history & memory")}</div>
          ) : (
            <div className="space-y-4 fade-up" data-testid="contact-detail">
              <div className="border border-gray-200 rounded-md p-4">
                <div className="font-heading font-bold text-lg text-gray-900">{detail.contact.name}</div>
                <div className="flex items-center gap-1.5 text-sm text-gray-500 mt-0.5"><EnvelopeSimple size={14} /> {detail.contact.email}</div>
                {detail.contact.organization && <div className="flex items-center gap-1.5 text-sm text-gray-500 mt-0.5"><Buildings size={14} /> {detail.contact.organization}</div>}
                {detail.contact.tone && <div className="mt-2 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded px-2 py-1 inline-block">{t("con.tone", "Agent tone:")} {detail.contact.tone}</div>}
              </div>
              <div className="border border-gray-200 rounded-md p-4">
                <div className="flex items-center gap-1.5 font-semibold text-sm text-gray-800 mb-2"><Brain size={15} className="text-blue-600" /> {t("con.memory", "Agent memory")}</div>
                {[...detail.memories, ...detail.org_memories].map((m) => (
                  <div key={m.id} className="text-[13px] text-gray-700 bg-gray-50 border border-gray-100 rounded px-2.5 py-1.5 mb-1.5">
                    <span className="text-[10px] font-semibold uppercase text-blue-600 mr-1.5">{m.scope}</span>{m.content}
                  </div>
                ))}
                {detail.memories.length + detail.org_memories.length === 0 && <p className="text-xs text-gray-400">{t("con.no_memories", "No memories yet — ask the agent to \"remember\" things.")}</p>}
              </div>
              <div className="border border-gray-200 rounded-md p-4">
                <div className="flex items-center gap-1.5 font-semibold text-sm text-gray-800 mb-2"><ClockCounterClockwise size={15} className="text-blue-600" /> {t("con.history", "Email history")} ({detail.history.length})</div>
                {detail.history.slice(0, 8).map((h) => (
                  <div key={h.id} className="text-[13px] border-b border-gray-100 py-1.5 last:border-0">
                    <span className="text-gray-400 text-xs mr-2">{dayjs(h.created_at).format("MMM D")}</span>
                    <span className="text-gray-800">{h.subject}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </PageShell>
  );
}
